import { type OptionItem } from "@common/declarations";
import { type I18n, i18nFormat } from "@common/lib/i18n";
import { identityPromise } from "@common/lib/misc";
import { countNodes, traverseTree } from "@common/lib/tree";
import { Common } from "@common/types";
import { isFunction, isNull, isUndefined } from "lodash";
import { type LazySelectComponentProps } from "../../hocs/withStatic";
import { type ValueType } from "../lib";
import { type MultiSelectContentProps, type MultiSelectOptionItemBase } from "./component";

export const getAllState = <T extends ValueType>(props: MultiSelectContentProps<T>): boolean | null => {
  const options = isFunction(props.options) ? props.options() : props.options;
  let count: Common.GenericMap<number> = {
    false: 0,
    "": 0,
    true: 0
  };
  const root: OptionItem<any> = {
    label: "",
    value: "",
    children: options
  };
  traverseTree<MultiSelectOptionItemBase<T>>(root as any, (_, x) => {
    count[(x.isSelected ?? "").toString()]++;
  });

  if (!count["false"] && !count["true"]) return null;
  if (!count["false"] && !count["null"]) return true;
  return false;
};

export const getIsUnselectable = <T extends ValueType>(opt: MultiSelectOptionItemBase<T>, level: number): boolean => {
  switch (opt.type) {
    case "group":
      return true;
    case "parent":
      return false;
    default:
      return level === 1 && !!opt.children?.length;
  }
};

export const getSelectedLabel = <T extends ValueType>(
  i18n: I18n,
  props: MultiSelectContentProps<T> & LazySelectComponentProps,
  noSelectionLabel?: string
): string => {
  if (props.loadMore !== identityPromise && !isUndefined(props.selected)) return `${props.selected} ${i18n.selected}`;

  const match = (x: MultiSelectOptionItemBase<T>, level: number) =>
    !getIsUnselectable(x, level) && x.type !== "parent" && x.type !== "group";

  const options = isFunction(props.options) ? props.options() : props.options;
  const selected = countNodes(options, (x, level) => match(x, level) && !!x.isSelected);

  if (!selected && noSelectionLabel) return noSelectionLabel;

  return i18nFormat(i18n.mOfNSelected, ["M", selected], ["N", countNodes(options, match)]);
};

export const getSelectAllLabel = <T extends ValueType>(
  i18n: I18n,
  props: MultiSelectContentProps<T> & LazySelectComponentProps
): string => {
  const allState = getAllState(props);

  const base = isNull(allState)
    ? props.allCaptions?.deselect ?? i18n.deselectAll
    : allState
    ? props.allCaptions?.null ?? i18n.deselectAll
    : props.allCaptions?.select ?? i18n.selectAll;
  return props.allCaptions?.isNoNumbers ? base : `${base} (${getSelectedLabel(i18n, props)})`;
};
